import { Stack, Typography } from '@mui/joy';
import { t } from 'i18next';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { LabeledValue, TextHighlighter } from 'core/elements';
import { GetManyResponse, IAutocompleteOption, IRadioChoice, IRequestPayload } from 'core/types';
import { TProjectRenderOptions } from 'projects/components/ProjectFormField/ProjectFormField';
import { ExternalProjectAssociationType } from 'projects/enums';
import { IProjectAutocompleteRequestPayload } from 'projects/types';
import { getActiveExternalProjectAccounts } from 'projects/utils';
import { SpaStatus } from 'quotes/enums';
import { Color, Level } from 'theme/enums';

import { SpaPriorityTier } from '../../enums';
import { IAttributeFormLocation } from '../../types';

export const PROJECT_REQUEST_PAYLOAD: IProjectAutocompleteRequestPayload = {
  filters: { hasExternalProject: 1 },
};

// Only users flagged as Trade Agreement Owners are eligible; CorporateUserFormField merges its
// active-only filter on top. `company_id` is deliberately absent — the BE forces the session
// company and overrides anything sent.
export const TRADE_AGREEMENT_OWNER_REQUEST_PAYLOAD: IRequestPayload = {
  filters: { isTradeAgreementOwner: 1 },
};

const LABELED_VALUE_OPTIONS = {
  labelLevel: Level.BODY_SM,
  labelSx: { fontWeight: 400 },
  inline: true,
} as const;

export const renderProjectOption: TProjectRenderOptions = (option, _getLabel, inputValue) => {
  const billToAccounts = getActiveExternalProjectAccounts(option.externalProjectAccounts).filter(
    (account) => account.associationType === ExternalProjectAssociationType.BILL_TO_ACCOUNT
  );
  const firstAccount = billToAccounts[0];
  const customerName = firstAccount?.customerName || DEFAULT_PLACEHOLDER;
  const accountNumber = firstAccount?.accountNumber || DEFAULT_PLACEHOLDER;
  const additionalAccountsCount = billToAccounts.length - 1;

  return (
    <Stack sx={{ width: '100%' }}>
      <TextHighlighter
        level={Level.TITLE_MD}
        text={option.name || DEFAULT_PLACEHOLDER}
        highlight={inputValue}
      />

      {option.externalProjectNumber && (
        <LabeledValue
          label={t('labelExternalProjectNumber')}
          value={<TextHighlighter text={option.externalProjectNumber} highlight={inputValue} />}
          options={LABELED_VALUE_OPTIONS}
        />
      )}

      {option.externalProgramNumber && (
        <LabeledValue
          label={t('labelExternalProgramNumber')}
          value={<TextHighlighter text={option.externalProgramNumber} highlight={inputValue} />}
          options={LABELED_VALUE_OPTIONS}
        />
      )}

      <LabeledValue
        label={t('labelARNumber')}
        value={<TextHighlighter text={accountNumber} highlight={inputValue} />}
        options={LABELED_VALUE_OPTIONS}
      />

      <LabeledValue
        label={t('labelCustomerName')}
        value={<TextHighlighter text={customerName} highlight={inputValue} />}
        options={LABELED_VALUE_OPTIONS}
      />

      {additionalAccountsCount > 0 && (
        <Typography level={Level.BODY_SM} color={Color.PRIMARY}>
          {t('labelMoreCount', { count: additionalAccountsCount })}
        </Typography>
      )}
    </Stack>
  );
};

export const renderLocationId = (item: IAutocompleteOption) => (
  <LabeledValue
    label={t('labelLocationId')}
    value={item.externalLocationId}
    options={{ inline: true, labelLevel: Level.BODY_SM, valueLevel: Level.BODY_SM }}
  />
);

// Sales locations only: a location's owner feeds the Trade Agreement Owner prefill, so the chip
// surfaces it; locations without one (and all shipping locations) show just the id line.
export const renderSalesLocationSecondary = (item: IAttributeFormLocation) => (
  <Stack>
    {renderLocationId(item)}
    {item.tradeAgreementOwner?.name && (
      <LabeledValue
        label={t('labelTradeAgreementOwner')}
        value={item.tradeAgreementOwner.name}
        options={{ inline: true, labelLevel: Level.BODY_SM, valueLevel: Level.BODY_SM }}
      />
    )}
  </Stack>
);

export const renderTradeAgreementOwnerOption = (
  option: IAutocompleteOption,
  _getLabel: (option: IAutocompleteOption) => string,
  inputValue?: string
) => (
  <Stack sx={{ width: '100%' }}>
    <TextHighlighter
      level={Level.TITLE_MD}
      text={option.name || DEFAULT_PLACEHOLDER}
      highlight={inputValue}
    />

    {option.email && (
      <TextHighlighter level={Level.BODY_SM} text={option.email} highlight={inputValue} />
    )}
  </Stack>
);

export const renderArNumber = (item: IAutocompleteOption) => (
  <LabeledValue
    label={t('labelARNumber')}
    value={item.accountsReceivableId}
    options={{ inline: true, labelLevel: Level.BODY_SM, valueLevel: Level.BODY_SM }}
  />
);

export const getSpaStatusChoices = (): IRadioChoice[] => [
  { value: SpaStatus.PLANNING, label: t('labelSpaStatusPlanning') },
  { value: SpaStatus.LIVE, label: t('labelSpaStatusLive') },
];

export const getSpaPriorityTierOptions = () => [
  { value: SpaPriorityTier.PRIORITY_1, label: t('labelSpaPriority1') },
  { value: SpaPriorityTier.PRIORITY_2, label: t('labelSpaPriority2') },
  { value: SpaPriorityTier.PRIORITY_3, label: t('labelSpaPriority3') },
];

interface IPrependSentinelOptionArgs<T> {
  response: GetManyResponse<T>;
  isFirstPage: boolean;
  label: string;
  query: string | undefined;
  buildOption: () => T;
}

// Prepend a sentinel option, only on the first page and only when the query is empty
// or matches the sentinel's label (empty/whitespace query matches via `includes('')`).
export const prependSentinelOption = <T,>({
  response,
  isFirstPage,
  label,
  query,
  buildOption,
}: IPrependSentinelOptionArgs<T>): GetManyResponse<T> => {
  const searchQuery = query?.trim().toLowerCase() ?? '';
  const matchesLabel = label.toLowerCase().includes(searchQuery);

  if (!isFirstPage || !matchesLabel) {
    return response;
  }

  return {
    ...response,
    records: [buildOption(), ...response.records],
  };
};

interface IIncludeSelectedOptionArgs<T extends { id?: number }> {
  response: GetManyResponse<T>;
  isFirstPage: boolean;
  selected: T | null | undefined;
}

// MUI warns when the value is missing from the loaded options, which a stored value beyond the
// first page always is. The selected record leads page 1 and is dropped from every page, so it
// never shows twice.
export const includeSelectedOption = <T extends { id?: number }>({
  response,
  isFirstPage,
  selected,
}: IIncludeSelectedOptionArgs<T>): GetManyResponse<T> => {
  if (!selected?.id) {
    return response;
  }

  const records = (response?.records ?? []).filter((record) => record?.id !== selected.id);

  return {
    ...response,
    records: isFirstPage ? [selected, ...records] : records,
  };
};
